import type { Scan, ScanConfirmation, ScanConfirmOptions } from "@hobbyshopify/backend-client";
import { BaseController, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { confirmScan, pickScanCandidate, rejectScan, saveScanDraft } from "../../core/ops/scan-ops";
import { errorText } from "../../core/primitives/errors";
import {
  buildConfirmBody,
  buildCorrections,
  type EditState,
  isFloorValid,
  isYearValid,
  toEditState,
} from "../../core/primitives/scan-edit";
import { ScanEditorActions, ScanEditorState } from "./scan-editor-model";

export type ScanEditorDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "locationsRepository" | "reviewQueueRepository" | "customersRepository"
>;

/** What the owning page does once the scan is answered. */
export interface ScanEditorHost {
  onConfirmed(edit: EditState, result: ScanConfirmation): void;
  onRejected(): void;
}

/**
 * Long enough that a typed word is one save rather than six, short enough that
 * the match has moved by the time the reviewer looks up from the field.
 */
export const DRAFT_SAVE_DELAY_MS = 900;

/**
 * Reviewing one scan: correct the reading, then confirm it. A section, because
 * the scan page and the edit page share it whole.
 *
 * Confirming is the only thing in the app that creates a `Card`, which is why
 * this screen is the gate rather than a formality, and why rejecting is just a
 * state change with nothing to clean up behind it.
 */
export class ScanEditorController extends BaseController implements ScanEditorActions {
  readonly state = new ScanEditorState();
  #draftTimer: ReturnType<typeof setTimeout> | null = null;
  /** The corrections last sent, so an unchanged form does not save again. */
  #lastSent = "{}";
  /**
   * Until the reviewer types a price, a refetched estimate replaces what is in
   * the field: that is the point of refetching. After, nothing overwrites it.
   */
  #priceTouched = false;

  constructor(
    subtree: SubtreeModel,
    readonly deps: ScanEditorDeps,
    readonly host: ScanEditorHost,
  ) {
    super();
    subtree.put(ScanEditorState, this.state);
    subtree.put(ScanEditorActions, this);

    this.sync(() => {
      this.state.quoteValidityMinutes.value = deps.storeRepository.selectedStore?.quoteValidityMinutes ?? null;
    }, [deps.storeRepository]);
    this.sync(() => {
      // The counter the app is working at first, so stock lands there unless the merchant says otherwise.
      const here = deps.storeRepository.locationId;
      const all = deps.locationsRepository.locations;
      this.state.locations.value = [...all.filter((l) => l.id === here), ...all.filter((l) => l.id !== here)];
    }, [deps.locationsRepository, deps.storeRepository]);

    this.sync(() => {
      const scan = this.state.scan.value;
      const edit = this.state.edit.value;
      const pending = scan?.review === "PENDING" && scan.recognitionOutcome !== "no_match" && edit !== null;
      const blocker = !pending
        ? null
        : !isYearValid(edit.year)
          ? "Enter a valid year to confirm."
          : !isFloorValid(edit)
            ? "Set the floor price at or below the asking price to confirm."
            : null;
      this.state.confirmBlocker.value = blocker;
      this.state.canConfirm.value = !this.state.busy.value && pending && blocker === null;
    }, [this.state.scan, this.state.edit, this.state.busy]);

    this.subscribe(() => this.#scheduleDraftSave(), [this.state.edit]);
    this.autoDispose(() => {
      if (this.#draftTimer) clearTimeout(this.#draftTimer);
    });
  }

  /** The scan to review. Seeds the form once; later draft answers never overwrite what was typed since. */
  seed(scan: Scan): void {
    if (this.#draftTimer) clearTimeout(this.#draftTimer);
    this.#draftTimer = null;
    this.#lastSent = "{}";
    this.#priceTouched = false;
    this.state.scan.value = scan;
    this.state.edit.value = scan.recognitionOutcome === "no_match" ? null : toEditState(scan);
    this.state.draftError.value = null;
    this.state.picking.value = null;
    this.state.error.value = null;
    this.state.askingStock.value = false;
    this.state.zoomIndex.value = null;
  }

  /**
   * The pick is one of the products the lookup already weighed, so switching
   * costs no lookup — and the estimate follows it by the same rule a re-price
   * uses, since the figure in the field was the old product's.
   */
  pick = async (productId: string) => {
    const scan = this.state.scan.value;
    const shopId = this.deps.storeRepository.shopId;
    if (
      !scan ||
      shopId === null ||
      scan.review !== "PENDING" ||
      scan.recognitionOutcome === "no_match" ||
      this.state.picking.value !== null
    )
      return;
    this.state.picking.value = productId;
    this.state.draftError.value = null;
    try {
      const r = await pickScanCandidate({ ...this.deps, shopId, scanId: scan.id, productId });
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.draftError.value = errorText(r.errors) || "Could not switch the card.";
        return;
      }
      this.state.scan.value = r.value.scan;
      if (!this.#priceTouched) {
        const reseeded = toEditState(r.value.scan);
        this.state.edit.update((prev) => (prev ? { ...prev, price: reseeded.price } : prev));
      }
    } finally {
      if (!this.isDisposed) this.state.picking.value = null;
    }
  };

  change = (updates: Partial<EditState>) => {
    if (updates.price !== undefined) this.#priceTouched = true;
    this.state.edit.update((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  /**
   * Confirming opens step two rather than saving: identity and price are
   * settled, and whether the shop counts copies is the next question.
   *
   * **Except for a customer's scan, which skips it.** That is one person asking
   * this shop for a price, and confirming is the shop answering: it does not
   * claim the shop took a card in, so there is no lot to record and nothing to
   * ask about. A card the shop already tracks is unaffected: confirming without
   * a lot never turns tracking off, and the quote still reserves a copy.
   */
  primary = () => {
    if (!this.state.canConfirm.value) return;
    if (this.state.scan.value?.kind === "CUSTOMER") {
      void this.confirm();
      return;
    }
    this.state.askingStock.value = true;
  };

  /**
   * Step two's answer, applied. `stock` present is what the backend reads as
   * "track this card", so the two buttons differ by exactly that, and both of
   * them confirm.
   */
  confirm = async (stock?: { quantity: number; locationId?: string }) => {
    const scan = this.state.scan.value;
    const edit = this.state.edit.value;
    const shopId = this.deps.storeRepository.shopId;
    if (!scan || !edit || shopId === null || !this.state.canConfirm.value) return;
    const body: ScanConfirmOptions = { ...buildConfirmBody(scan, edit), ...(stock ? { stock } : {}) };

    this.state.busy.value = true;
    this.state.error.value = null;
    try {
      const r = await confirmScan({ ...this.deps, shopId, scanId: scan.id, body });
      if (this.isDisposed) return;
      this.state.askingStock.value = false;
      if (!r.ok) {
        // A scan is confirmable exactly once, so a retry after a genuine failure
        // is safe but a retry after a success is refused; the message tells them apart.
        this.state.error.value = errorText(r.errors);
        return;
      }
      this.host.onConfirmed(edit, r.value);
    } finally {
      this.state.busy.value = false;
    }
  };

  cancelStock = () => {
    this.state.askingStock.value = false;
  };

  reject = async () => {
    const scan = this.state.scan.value;
    const shopId = this.deps.storeRepository.shopId;
    if (
      !scan ||
      shopId === null ||
      this.state.busy.value ||
      scan.review !== "PENDING" ||
      scan.recognitionOutcome === "no_match"
    )
      return;
    this.state.busy.value = true;
    this.state.error.value = null;
    try {
      const r = await rejectScan({ ...this.deps, shopId, scanId: scan.id });
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.error.value = errorText(r.errors);
        return;
      }
      this.host.onRejected();
    } finally {
      this.state.busy.value = false;
    }
  };

  openZoom = (index: number) => {
    this.state.zoomIndex.value = index;
  };

  closeZoom = () => {
    this.state.zoomIndex.value = null;
  };

  /**
   * Saves the reading while the reviewer works, so the match and the price
   * follow the correction instead of waiting for confirm.
   *
   * Debounced rather than per keystroke, and the reason is upstream: an edit to
   * a field the pricing source was queried on costs a real lookup, and that
   * queue runs at one request every two seconds shared with another app. Most
   * corrections cost nothing, but the screen cannot know which until it asks.
   *
   * A failure never blocks confirming: the draft is a convenience, and
   * `confirm` sends the corrections itself either way.
   */
  #scheduleDraftSave(): void {
    if (this.#draftTimer) clearTimeout(this.#draftTimer);
    const scan = this.state.scan.value;
    const edit = this.state.edit.value;
    if (!scan || !edit || scan.review !== "PENDING" || scan.recognitionOutcome === "no_match") return;

    const corrections = buildCorrections(scan, edit);
    const key = JSON.stringify(corrections);
    if (key === "{}" || key === this.#lastSent) return;
    // A half-typed year is not a correction worth a round trip, and the backend
    // would key the card on it.
    if (!isYearValid(edit.year)) return;

    this.#draftTimer = setTimeout(() => {
      this.#draftTimer = null;
      this.#lastSent = key;
      void this.#saveDraft(scan.id, corrections);
    }, DRAFT_SAVE_DELAY_MS);
  }

  async #saveDraft(scanId: string, corrections: ReturnType<typeof buildCorrections>): Promise<void> {
    const shopId = this.deps.storeRepository.shopId;
    if (shopId === null) return;
    this.state.saving.value = true;
    this.state.draftError.value = null;
    const r = await saveScanDraft({ ...this.deps, shopId, scanId, corrections });
    if (this.isDisposed) return;
    this.state.saving.value = false;

    if (!r.ok) {
      // Let the next edit try again rather than wedging on a stale key.
      this.#lastSent = "{}";
      this.state.draftError.value = errorText(r.errors) || "Could not save that change.";
      return;
    }

    const edit = this.state.edit.value;
    this.state.scan.value = r.value.scan;
    if (edit) this.#lastSent = JSON.stringify(buildCorrections(r.value.scan, edit));
    if (r.value.repriced && !this.#priceTouched) {
      const reseeded = toEditState(r.value.scan);
      this.state.edit.update((prev) => (prev ? { ...prev, price: reseeded.price } : prev));
    }
  }
}
